import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../../data";
import { Icon } from "../../../components/Icon";
import { today as todayIn } from "../../../lib/format";
import { t } from "../../../lib/sprache";
import { FadenZeile, ThreadPanel } from "./chat";
import { loadAgencyThreads } from "./data";
import { setMiniFaden } from "./hinweis";
import { qk, useMembers, useUnreadData } from "./hooks";
import { shortWhen, threadLines, visibleThreadLines, type MessageRow } from "./model";
import "./nachrichten.css";

/**
 * K-524: Chat überall erreichbar. Unten rechts ein runder Knopf mit der Zahl ungelesener Nachrichten; er öffnet ein kleines
 * Fenster mit den Unterhaltungen und dem kompakten Verlauf samt Eingabe (derselbe Baustein wie auf der Chatseite). Auf der
 * Chatseite selbst fehlt der Knopf. Am Handy (unter 768 px) schwebt nichts: der Knopf führt auf die Chatseite. Liegt Coco
 * unten rechts (CocoLugt), sitzt der Knopf darüber; die Abstände regelt nachrichten.css.
 */
const SCHMAL = "(max-width: 767px)";

function useSchmal(): boolean {
  const [schmal, setSchmal] = useState(() => typeof window !== "undefined" && !!window.matchMedia?.(SCHMAL).matches);
  useEffect(() => {
    const mm = window.matchMedia?.(SCHMAL);
    if (!mm) return;
    const f = () => setSchmal(mm.matches);
    f();
    mm.addEventListener("change", f);
    return () => mm.removeEventListener("change", f);
  }, []);
  return schmal;
}

export function MiniChat({ orgId, timeZone }: { orgId: string; timeZone: string }) {
  const ort = useLocation();
  const schmal = useSchmal();
  const [offen, setOffen] = useState(false);
  const [talentId, setTalentId] = useState<string | null>(null);
  const [suche, setSuche] = useState("");
  const scope = useMemo(() => ({ portal: "agentur" as const, orgId }), [orgId]);
  const unread = useUnreadData(scope).data?.count.messages ?? 0;
  const aufChatseite = ort.pathname.startsWith("/agentur/nachrichten");
  const sichtbar = offen && !schmal && !aufChatseite;
  const threads = useQuery({ queryKey: qk.agencyThreads(orgId), queryFn: () => loadAgencyThreads(api, orgId), enabled: sichtbar });
  const members = useMembers(orgId, sichtbar);
  const fenster = useRef<HTMLDivElement>(null);
  const knopf = useRef<HTMLButtonElement>(null);
  const me = api.session()?.userId ?? null;

  // Der Hinweis bei neuer Nachricht entfällt für den Faden, den das Fenster gerade zeigt
  useEffect(() => {
    setMiniFaden(sichtbar ? talentId : null);
    return () => setMiniFaden(null);
  }, [sichtbar, talentId]);
  // Seitenwechsel (z. B. über eine Objekt-Karte im Verlauf) schließt das Fenster
  useEffect(() => {
    setOffen(false);
  }, [ort.pathname]);
  // Escape schließt, der Fokus kehrt zum Knopf zurück
  useEffect(() => {
    if (!sichtbar) return;
    const f = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || document.querySelector("dialog[open]")) return;
      setOffen(false);
      knopf.current?.focus();
    };
    document.addEventListener("keydown", f);
    return () => document.removeEventListener("keydown", f);
  }, [sichtbar]);
  useEffect(() => {
    if (sichtbar) window.setTimeout(() => fenster.current?.querySelector<HTMLElement>("input, textarea, button")?.focus(), 0);
  }, [sichtbar, talentId]);

  // UI-A4 (UI-Prüfung 27.10.2026): der Knopf verdeckte Zeilenaktionen unten rechts. Beim Herunterscrollen weicht er aus, beim
  // Hochscrollen oder oben auf der Seite ist er wieder da; geöffnet bleibt er stehen
  const weg = useAusweichenBeimScrollen(!sichtbar);
  const lines = useMemo(() => (threads.data ? threadLines(threads.data.threads, threads.data.talents, "agency") : []), [threads.data]);
  const memberName = useMemo(() => new Map((members.data ?? []).map((m) => [m.user_id, m.display_name])), [members.data]);
  if (aufChatseite) return null;

  const zahl = unread > 99 ? "99+" : String(unread);
  const label = unread
    ? t(`Chat öffnen, ${unread} ungelesen`, `Open chat, ${unread} unread`)
    : t("Chat öffnen", "Open chat");
  if (schmal) {
    return (
      <Link className={`tk-chat-fab tk-minichat-knopf tk-noprint${weg ? " tk-fab-weg" : ""}`} to="/agentur/nachrichten" aria-label={label} title={label}>
        <Icon name="chat" />
        {unread > 0 && <span className="tk-chat-fab-badge" aria-hidden="true">{zahl}</span>}
      </Link>
    );
  }

  const gewaehlt = talentId ? lines.find((l) => l.talentId === talentId) ?? null : null;
  const today = todayIn(timeZone);
  const shown = visibleThreadLines(lines, suche, "alle", null).slice(0, 40);
  const senderName = (m: MessageRow) =>
    m.sender_side === "agency" && m.sender_user_id && m.sender_user_id !== me ? memberName.get(m.sender_user_id) ?? t("Team", "Team") : null;

  return (
    <>
      {sichtbar && (
        <div ref={fenster} className="tk-minichat tk-noprint" role="dialog" aria-modal="false" aria-labelledby="minichat-titel">
          <div className="tk-minichat-kopf">
            <strong id="minichat-titel">{t("Chat", "Chat")}</strong>
            <small>{unread ? t(`${unread} ungelesen`, `${unread} unread`) : t("alles gelesen", "all read")}</small>
            <Link className="tk-minichat-gross" to={talentId ? `/agentur/nachrichten/${talentId}` : "/agentur/nachrichten"}>
              {t("Groß öffnen", "Open full view")}
            </Link>
            <button type="button" className="tk-faden-knopf" onClick={() => { setOffen(false); knopf.current?.focus(); }} aria-label={t("Chat schließen", "Close chat")} title={t("Chat schließen", "Close chat")}>×</button>
          </div>
          {talentId && gewaehlt ? (
            <ThreadPanel
              key={talentId}
              compact
              portal="agentur"
              orgId={orgId}
              talentId={talentId}
              title={gewaehlt.name}
              titleHref={`/agentur/creator/${talentId}`}
              timeZone={timeZone}
              talentName={gewaehlt.name}
              senderName={senderName}
              memberName={(id) => (id ? memberName.get(id) ?? null : null)}
              onBack={() => setTalentId(null)}
            />
          ) : (
            <div className="tk-minichat-liste">
              <label className="tk-minichat-suche tk-chat-list-suche">
                <span className="sr-only">{t("Creator suchen", "Search creators")}</span>
                <Icon name="search" />
                <input type="search" value={suche} onChange={(e) => setSuche(e.target.value)} placeholder={t("Creator suchen …", "Search creators …")} />
              </label>
              {threads.isPending && <p className="muted">{t("Unterhaltungen werden geladen …", "Loading conversations …")}</p>}
              {threads.error && <p className="tk-error" role="alert">{t("Die Unterhaltungen ließen sich nicht laden.", "The conversations could not be loaded.")}</p>}
              {shown.map((l) => (
                <FadenZeile key={l.talentId} className="tk-minichat-zeile" name={l.name} unread={l.unread} onClick={() => setTalentId(l.talentId)}
                  when={l.lastAt ? shortWhen(l.lastAt, today, timeZone) : null}
                  preview={l.lastAt ? l.preview : t("Noch keine Nachrichten · Unterhaltung beginnen", "No messages yet · start a conversation")} />
              ))}
              {threads.data && shown.length === 0 && (
                <p className="muted">{suche.trim() ? t("Kein Creator gefunden.", "No creator found.") : t("Noch keine Unterhaltungen. Suche einen Creator, um zu schreiben.", "No conversations yet. Search for a creator to write.")}</p>
              )}
            </div>
          )}
        </div>
      )}
      <button ref={knopf} type="button" className={`tk-chat-fab tk-minichat-knopf tk-noprint${weg ? " tk-fab-weg" : ""}`} aria-expanded={sichtbar} aria-haspopup="dialog"
        aria-label={sichtbar ? t("Chat schließen", "Close chat") : label} title={sichtbar ? t("Chat schließen", "Close chat") : label}
        onClick={() => setOffen((v) => !v)}>
        <Icon name={sichtbar ? "chevron" : "chat"} />
        {unread > 0 && <span className="tk-chat-fab-badge" aria-hidden="true">{zahl}</span>}
      </button>
    </>
  );
}

/** Schwebender Knopf weicht beim Herunterscrollen aus (UI-A4); oben auf der Seite und beim Hochscrollen sichtbar */
function useAusweichenBeimScrollen(aktiv: boolean): boolean {
  const [weg, setWeg] = useState(false);
  useEffect(() => {
    if (!aktiv) { setWeg(false); return; }
    let zuletzt = window.scrollY;
    const f = () => {
      const y = window.scrollY;
      if (y < 120) setWeg(false);
      else if (y > zuletzt + 8) setWeg(true);
      else if (y < zuletzt - 8) setWeg(false);
      zuletzt = y;
    };
    window.addEventListener("scroll", f, { passive: true });
    return () => window.removeEventListener("scroll", f);
  }, [aktiv]);
  return weg;
}
